import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, Tag, Avatar, Select, DatePicker, Card, List, Spin, message } from 'antd';
import { SearchOutlined, MessageOutlined, UndoOutlined, MoreOutlined, CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import InfiniteScrollList from '../../../components/Forms/ListView/InfiniteScrollList';
import AddFeedbackModal from './AddFeedbackModal';
import SmallProfileCard from '../../../components/ContactInfo/SmallProfileCard';
import { useDispatch } from 'react-redux';
import { getData } from '../../../actions/apiCall';
import { postData } from '../../../actions/developer';
import axios from 'axios';
import { getAccess } from '../../../actions/auth';
import dayjs, { Dayjs } from 'dayjs';

// Types
interface FeedbackReply {
  _id?: string;
  reply: string;
  repliedBy: {
    _id: string;
    name: string;
    firstName?: string;
    lastName?: string;
    avatar?: string;
    role?: string;
    employeeId?: string;
    workEmail?: string;
  };
  createdAt: string | Date;
}

interface FeedbackEntry {
  id: string;
  _id: string;
  interviewer: string;
  role: string;
  status: 'Selected' | 'Rejected' | 'On Hold' | 'CLEARED' | 'REJECTED' | 'HOLD' | 'PENDING';
  date: string;
  time: string;
  level: string;
  comment: string;
  replies: FeedbackReply[];
  feedbackId?: string;
  accountName?: string;
  addedBy?: {
    _id: string;
    name: string;
    avatar?: string;
    role?: string;
    employeeId?: string;
    workEmail?: string;
  };
  developer?: {
    _id: string;
    name: string;
    firstName?: string;
    lastName?: string;
    avatar?: string;
    email?: string;
  };
  feedbackSource?: 'developer' | 'interviewer' | 'bde';
  createdAt?: string;
}

const FeedbackDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState('');
  const [benchFilter, setBenchFilter] = useState<string>('all'); // 'vendor', 'own', 'all'
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
  const [reloadEmployees, setReloadEmployees] = useState(false);
  const [isAddFeedbackModalVisible, setIsAddFeedbackModalVisible] = useState(false);
  const [selectedInterviewForFeedback, setSelectedInterviewForFeedback] = useState<any>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Filter states
  const [companyFilter, setCompanyFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [levelFilter, setLevelFilter] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  // Data states
  const [companies, setCompanies] = useState<{ value: string; label: string }[]>([]);
  const [interviewLevels, setInterviewLevels] = useState<{ value: string; label: string }[]>([]);
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState<string>('');
  const [reloadFeedbacks, setReloadFeedbacks] = useState(false);

  // Debounce search query
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Trigger reload when filter or debounced search changes
  useEffect(() => {
    setReloadEmployees(true);
  }, [benchFilter, debouncedSearchQuery]);

  // Fetch interview levels from backend enum
  useEffect(() => {
    const fetchInterviewLevels = async () => {
      try {
        const access = getAccess();
        const response = await axios.get('/api/calendar/interviews/levels', {
          headers: { Authorization: `Bearer ${access}` }
        });
        // Backend returns { code: 200, data: { data: [{ value, label }, ...] } }
        if (response.data?.code === 200 || response.data?.success) {
          const levelsData = response.data?.data?.data || response.data?.data || [];
          // If it's already in options format, use it directly; otherwise convert
          const levelOptions = Array.isArray(levelsData) && levelsData.length > 0 && typeof levelsData[0] === 'object' && 'value' in levelsData[0]
            ? levelsData
            : levelsData.map((level: string) => ({
              value: level,
              label: level
            }));
          setInterviewLevels(levelOptions);
        }
      } catch (error) {
        console.error('Error fetching interview levels:', error);
      }
    };
    fetchInterviewLevels();
  }, []);

  // Fetch accounts (businesses) for filter
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await dispatch(getData('/accounts'));
        if (response?.success || response?.code === 200) {
          const accountsData = response?.data || response?.data?.data || [];
          const accountOptions = Array.isArray(accountsData)
            ? accountsData.map((account: any) => ({
              value: account._id,
              label: account.businessName || account.name || 'N/A'
            }))
            : [];
          setCompanies(accountOptions);
        }
      } catch (error) {
        console.error('Error fetching accounts:', error);
      }
    };
    fetchAccounts();
  }, [dispatch]);

  // Trigger reload when filters or selected employee changes
  useEffect(() => {
    if (selectedEmployeeId) {
      setReloadFeedbacks(true);
    } else {
      setSelectedEmployee(null);
    }
  }, [selectedEmployeeId, companyFilter, statusFilter, levelFilter, dateRange]);

  // Fetch selected employee details
  useEffect(() => {
    const fetchEmployeeDetails = async () => {
      if (!selectedEmployeeId) {
        setSelectedEmployee(null);
        return;
      }
      try {
        const response = await dispatch(getData(`/bench-developers/${selectedEmployeeId}`));
        if (response?.success || response?.code === 200) {
          setSelectedEmployee(response?.data || response?.data?.data);
        }
      } catch (error) {
        console.error('Error fetching employee details:', error);
      }
    };
    fetchEmployeeDetails();
  }, [selectedEmployeeId, dispatch]);

  const transformFeedbackItem = (item: any): FeedbackEntry => {
    // Determine feedback source:
    // - If addedBy matches the developer's user account, it's from developer
    // - If addedBy role contains 'bde', it's from BDE
    // - Otherwise, it's from interviewer
    let feedbackSource: 'developer' | 'interviewer' | 'bde' = 'interviewer';
    if (item.developer?.developer?._id && item.addedBy?._id &&
      String(item.developer.developer._id) === String(item.addedBy._id)) {
      feedbackSource = 'developer';
    } else if (item.addedBy?.role?.toLowerCase().includes('bde') ||
      item.addedBy?.role?.toLowerCase().includes('business development')) {
      feedbackSource = 'bde';
    }

    return {
      id: item._id,
      _id: item._id,
      feedbackId: item._id,
      interviewer: item.addedBy?.name || 'N/A',
      role: item.addedBy?.role || item.company?.name || 'N/A',
      status: mapStatusToDisplay(item.status),
      date: item.interviewDate ? dayjs(item.interviewDate).format('DD MMM, YYYY') : (item.feedbackDateTime ? dayjs(item.feedbackDateTime).format('DD MMM, YYYY') : 'N/A'),
      time: item.interviewTime ? (() => {
        const [hours, minutes] = item.interviewTime.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        return `${displayHour}:${minutes} ${ampm}`;
      })() : (item.feedbackDateTime ? dayjs(item.feedbackDateTime).format('hh:mm A') : 'N/A'),
      level: item.interviewLevel || 'N/A',
      comment: item.comments || '',
      accountName: item.accountName || null,
      replies: (item.replies || []).map((reply: any) => {
        // Handle case where repliedBy might be an ObjectId string instead of an object
        let repliedByData = reply.repliedBy;
        if (typeof repliedByData === 'string' || (repliedByData && !repliedByData.name && !repliedByData.firstName)) {
          // If repliedBy is just an ID or doesn't have name/firstName, set to null
          repliedByData = null;
        }

        return {
          _id: reply._id,
          reply: reply.reply || '',
          repliedBy: repliedByData ? {
            _id: repliedByData._id || reply.repliedBy?._id || '',
            name: repliedByData.name || (repliedByData.firstName && repliedByData.lastName
              ? `${repliedByData.firstName} ${repliedByData.lastName}`.trim()
              : repliedByData.firstName || repliedByData.lastName || 'N/A'),
            firstName: repliedByData.firstName,
            lastName: repliedByData.lastName,
            avatar: repliedByData.avatar,
            role: repliedByData.role,
            employeeId: repliedByData.employeeId || null,
            workEmail: repliedByData.workEmail || null
          } : {
            _id: typeof reply.repliedBy === 'string' ? reply.repliedBy : '',
            name: 'N/A',
            firstName: null,
            lastName: null,
            avatar: null,
            role: null,
            employeeId: null,
            workEmail: null
          },
          createdAt: reply.createdAt || new Date()
        };
      }),
      addedBy: item.addedBy,
      developer: item.developer,
      feedbackSource: feedbackSource,
      createdAt: item.feedbackDateTime
    };
  };

  const mapStatusToDisplay = (status: string): 'Selected' | 'Rejected' | 'On Hold' => {
    const statusUpper = status?.toUpperCase() || '';
    if (statusUpper === 'CLEARED' || statusUpper === 'SELECTED') return 'Selected';
    if (statusUpper === 'REJECTED') return 'Rejected';
    if (statusUpper === 'HOLD' || statusUpper.includes('HOLD')) return 'On Hold';
    return 'On Hold';
  };

  const handleReply = async (feedbackId: string) => {
    if (!replyText.trim()) {
      message.warning('Please enter a reply');
      return;
    }

    try {
      const response = await dispatch(
        postData(`/developer-feedback/${feedbackId}/reply`, { reply: replyText.trim() })
      );

      if (response?.success || response?.code === 200 || response?.code === 201) {
        message.success('Reply added successfully');
        setReplyingToId(null);
        setReplyText('');
        setReloadFeedbacks(true); // Trigger reload
      } else {
        message.error('Failed to add reply');
      }
    } catch (error: any) {
      console.error('Error adding reply:', error);
      message.error('Failed to add reply');
    }
  };

  const getStatusColor = (status: string) => {
    if (status === 'Selected') return 'success';
    if (status === 'Rejected') return 'error';
    return 'warning';
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 -mt-4">
        <h2 className="text-xl font-bold">Feedbacks</h2>
        <div className="flex gap-2">
          <Select
            placeholder="Company"
            className="w-32"
            value={companyFilter}
            onChange={setCompanyFilter}
            allowClear
            options={companies}
          />
          <Select
            placeholder="Status"
            className="w-28"
            value={statusFilter}
            onChange={setStatusFilter}
            allowClear
            options={[
              { value: 'CLEARED', label: 'Selected' },
              { value: 'REJECTED', label: 'Rejected' },
              { value: 'HOLD', label: 'On Hold' },
              { value: 'PENDING', label: 'Postpone' }
            ]}
          />
          <Select
            placeholder="Level"
            className="w-24"
            value={levelFilter}
            onChange={setLevelFilter}
            allowClear
            options={interviewLevels}
          />
          <DatePicker.RangePicker
            className="rounded-lg"
            style={{ width: 220 }}
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [Dayjs | null, Dayjs | null] | null)}
          />
        </div>
      </div>
      <div className="flex h-[calc(100vh-240px)] gap-4 font-sans">
        <div className="w-1/4 bg-white rounded-2xl shadow-sm flex flex-col overflow-hidden">
          <div className="p-4 border-b">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">Employees</h2>
              <Select
                value={benchFilter}
                style={{ width: 120 }}
                onChange={(value) => setBenchFilter(value)}
                size="small"
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'vendor', label: 'Vendor Bench' },
                  { value: 'own', label: 'Own Bench' }
                ]}
              />
            </div>
            <Input
              prefix={<SearchOutlined className="text-gray-400" />}
              placeholder="search"
              className="rounded-lg bg-gray-50 border-gray-200"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
            />
          </div>
          <div className="flex-1 overflow-hidden" style={{ minHeight: 0 }}>
            <InfiniteScrollList
              key={`${benchFilter}-${debouncedSearchQuery}`}
              apiUrl="/calendar/interview-employees"
              apiQuery={{
                page: 1,
                limit: 20,
                search: debouncedSearchQuery,
                ...(benchFilter === 'vendor' ? { vendorPartner: 'Vendor' } : {}),
                ...(benchFilter === 'own' ? { vendorPartner: 'Own' } : {})
              }}
              itemsPerPage={20}
              reload={reloadEmployees}
              onLoaded={() => setReloadEmployees(false)}
              renderItem={(employee: any, index: number) => {
                if (!employee || !employee._id) return null;

                const isSelected = selectedEmployeeId === employee._id;

                return (
                  <List.Item
                    key={employee._id || index}
                    className={`flex justify-between items-center hover:bg-blue-50 cursor-pointer transition-colors ${isSelected ? 'bg-[#E6F4FF] border-r-4 border-blue-500' : ''}`}
                    onClick={() => setSelectedEmployeeId(isSelected ? null : employee._id)}
                    style={{ padding: '12px 16px' }}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="bg-gray-300 text-gray-600 font-bold">
                        {employee.name?.charAt(0) || 'N'}
                      </Avatar>
                      <div>
                        <div className={`font-semibold text-sm ${isSelected ? 'text-blue-600' : 'text-gray-700'}`}>
                          {employee.name || 'N/A'}
                        </div>
                        <div className="text-xs text-gray-400">
                          {employee.vendorPartner && employee.vendorName
                            ? employee.vendorName
                            : (employee.employeeId || 'N/A')}
                        </div>
                      </div>
                    </div>
                  </List.Item>
                );
              }}
              listProps={{
                bordered: false,
              }}
            />
          </div>
        </div>

        <div className="flex-1 flex flex-col gap-4 overflow-y-auto">
          {selectedEmployeeId && selectedEmployee && (
            <div className="flex justify-between items-center py-2 px-4 bg-white rounded-lg">
              <SmallProfileCard
                name={selectedEmployee.name || `${selectedEmployee.firstName || ''} ${selectedEmployee.lastName || ''}`.trim() || 'N/A'}
                subTitle={(() => {
                  const role = selectedEmployee.role ||
                    (selectedEmployee.skills?.[0] && typeof selectedEmployee.skills[0] === 'object'
                      ? selectedEmployee.skills[0].skillName
                      : selectedEmployee.skills?.[0]) ||
                    selectedEmployee.jobTitle?.name ||
                    'Developer';

                  const experience = selectedEmployee.experience
                    ? `${selectedEmployee.experience} ${selectedEmployee.experience === 1 ? 'year' : 'years'} exp`
                    : '';

                  const vendorName = selectedEmployee.vendorPartner?.vendorName ||
                    (selectedEmployee.vendorPartner && typeof selectedEmployee.vendorPartner === 'object'
                      ? selectedEmployee.vendorPartner.vendorName
                      : null);

                  const parts = [role];
                  if (experience) parts.push(experience);
                  if (vendorName) parts.push(vendorName);

                  return parts.join(' • ') || 'Developer';
                })()}
                avatar={selectedEmployee.avatar || selectedEmployee.developer?.avatar}
                initials={
                  selectedEmployee.firstName?.charAt(0) ||
                  selectedEmployee.name?.charAt(0) ||
                  'N'
                }
              />
              <Button
                type="primary"
                ghost
                className="rounded-lg font-semibold border-blue-400"
                onClick={() => {
                  setSelectedInterviewForFeedback({
                    _id: selectedEmployeeId,
                    developer: {
                      firstName: selectedEmployee.firstName,
                      lastName: selectedEmployee.lastName,
                      email: selectedEmployee.email,
                      role: selectedEmployee.role ||
                        (selectedEmployee.skills?.[0] && typeof selectedEmployee.skills[0] === 'object'
                          ? selectedEmployee.skills[0].skillName
                          : selectedEmployee.skills?.[0]) ||
                        selectedEmployee.jobTitle?.name ||
                        'Developer'
                    },
                    interviewLevel: levelFilter || undefined
                  });
                  setIsAddFeedbackModalVisible(true);
                }}
              >
                Add Feedback
              </Button>
            </div>
          )}

          {selectedEmployeeId && !selectedEmployee && (
            <Card className="border-none" bodyStyle={{ padding: '16px 24px' }}>
              <Spin />
            </Card>
          )}

          {!selectedEmployeeId && (
            <Card className="border-none" bodyStyle={{ padding: '16px 24px' }}>
              <p className="text-gray-400 text-center">Select an employee to view feedbacks</p>
            </Card>
          )}

          {/* Feedback List */}
          {selectedEmployeeId ? (
            <div className="flex-1 overflow-hidden" style={{ minHeight: 0 }}>
              <InfiniteScrollList
                key={`${selectedEmployeeId}-${companyFilter}-${statusFilter}-${levelFilter}-${dateRange?.[0]?.format('YYYY-MM-DD')}-${dateRange?.[1]?.format('YYYY-MM-DD')}`}
                apiUrl={`/developer-feedback/${selectedEmployeeId}/get-feedback`}
                apiQuery={{
                  page: 1,
                  limit: 10,
                  ...(statusFilter ? { status: statusFilter } : {}),
                  ...(levelFilter ? { level: levelFilter } : {}),
                  ...(companyFilter ? { company: companyFilter } : {}),
                  ...(dateRange && dateRange[0] && dateRange[1] ? {
                    dateFrom: dateRange[0].format('YYYY-MM-DD'),
                    dateTo: dateRange[1].format('YYYY-MM-DD')
                  } : {})
                }}
                itemsPerPage={10}
                reload={reloadFeedbacks}
                onLoaded={() => setReloadFeedbacks(false)}
                renderItem={(item: any, index: number) => {
                  const feedbackItem = transformFeedbackItem(item);
                  return (
                    <Card key={feedbackItem.id} className="!h-auto border-none shadow-sm overflow-hidden mb-4" bodyStyle={{ padding: '24px', height: 'auto' }}>
                      {/* Interview Detail Header */}
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex flex-col gap-1">
                          <span className="text-[10px] text-gray-400 uppercase font-bold tracking-wider">Interview Detail</span>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                            <span className="flex items-center gap-2"><CalendarOutlined className="text-blue-500" /> {feedbackItem.date}</span>
                            <span className="flex items-center gap-2"><ClockCircleOutlined className="text-blue-500" /> {feedbackItem.time}</span>
                            {feedbackItem.accountName && (
                              <span className="flex items-center gap-2 text-blue-600 font-medium">
                                {feedbackItem.accountName}
                              </span>
                            )}
                            <Tag color={getStatusColor(feedbackItem.status)} className="rounded-md px-3 m-0">{feedbackItem.status}</Tag>
                          </div>
                        </div>
                        <Tag color="blue" className="rounded-md px-4 py-1 m-0 bg-blue-50 border-blue-100 text-blue-600 font-medium">
                          {feedbackItem.level}
                        </Tag>
                      </div>

                      <div className="border-t border-dashed border-gray-200 my-4" />

                      {/* Feedback Author Info */}
                      <div className="flex justify-between items-center mb-3">
                        <div className="flex items-center gap-3">
                          <Avatar
                            className="bg-gray-200"
                            src={feedbackItem.addedBy?.avatar}
                            icon={!feedbackItem.addedBy?.avatar ? <SearchOutlined /> : undefined}
                          >
                            {!feedbackItem.addedBy?.avatar && feedbackItem.interviewer?.charAt(0)}
                          </Avatar>
                          <div>
                            <h4 className="font-bold text-sm m-0">{feedbackItem.interviewer}</h4>
                            <span className="text-xs text-gray-400">{feedbackItem.role}</span>
                            {feedbackItem.addedBy && (feedbackItem.addedBy.employeeId || feedbackItem.addedBy.workEmail) && (
                              <span className="text-xs text-gray-400 ml-2">
                                ({feedbackItem.addedBy.employeeId || feedbackItem.addedBy.workEmail})
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="text-xs text-gray-400">{feedbackItem.date} {feedbackItem.time && `at ${feedbackItem.time}`}</span>
                      </div>

                      {/* Comment */}
                      <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">
                        "{feedbackItem.comment}"
                      </p>

                      {/* All Replies */}
                      {feedbackItem.replies && feedbackItem.replies.length > 0 && (
                        <div className="mt-4 space-y-3">
                          {feedbackItem.replies.map((reply, replyIndex) => (
                            <div key={reply._id || replyIndex} className="ml-8 p-4 bg-gray-50 rounded-xl border border-gray-100">
                              <div className="flex justify-between items-center mb-2">
                                <div className="flex items-center gap-2">
                                  <Avatar
                                    size="small"
                                    src={reply.repliedBy?.avatar}
                                    className="bg-gray-200"
                                  >
                                    {!reply.repliedBy?.avatar && reply.repliedBy?.name?.charAt(0)}
                                  </Avatar>
                                  <span className="font-bold text-xs">{reply.repliedBy?.name || 'N/A'}</span>
                                  {reply.repliedBy && (reply.repliedBy.employeeId || reply.repliedBy.workEmail) && (
                                    <span className="text-[10px] text-gray-400">
                                      {reply.repliedBy.employeeId || reply.repliedBy.workEmail}
                                    </span>
                                  )}
                                  {reply.repliedBy?.role && (
                                    <Tag className="text-[10px]">{reply.repliedBy.role}</Tag>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] text-gray-400">
                                    {dayjs(reply.createdAt).format('DD MMM, YYYY [at] hh:mm A')}
                                  </span>
                                </div>
                              </div>
                              <p className="text-xs text-gray-600 italic">"{reply.reply}"</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Reply Section / Action */}
                      {replyingToId === feedbackItem.id ? (
                        <div className="mt-4">
                          <Input.TextArea
                            rows={4}
                            placeholder="Type your reply"
                            className="rounded-xl border-gray-200"
                            maxLength={500}
                            showCount
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                          />
                          <div className="flex justify-end gap-2 mt-5">
                            <Button
                              className="rounded-lg border-gray-300"
                              onClick={() => {
                                setReplyingToId(null);
                                setReplyText('');
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="primary"
                              className="rounded-lg bg-blue-500"
                              onClick={() => handleReply(feedbackItem.feedbackId || feedbackItem.id)}
                            >
                              Send
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-end mt-4">
                          <Button
                            size="small"
                            type="text"
                            icon={<UndoOutlined />}
                            className="text-gray-400 text-xs"
                            onClick={() => setReplyingToId(feedbackItem.id)}
                          >
                            Reply
                          </Button>
                        </div>
                      )}
                    </Card>
                  );
                }}
                listProps={{
                  bordered: false,
                }}
              />
            </div>
          ) : (
            <Card className="border-none" bodyStyle={{ padding: '24px' }}>
              <p className="text-gray-400 text-center">Select an employee to view feedbacks</p>
            </Card>
          )}
        </div>

        {/* Add Feedback Modal */}
        <AddFeedbackModal
          visible={isAddFeedbackModalVisible}
          onClose={() => {
            setIsAddFeedbackModalVisible(false);
            setSelectedInterviewForFeedback(null);
          }}
          onSuccess={() => {
            // Refresh feedback list or show success message
            setIsAddFeedbackModalVisible(false);
            setSelectedInterviewForFeedback(null);
            setReloadFeedbacks(true); // Refresh feedback list after adding feedback
          }}
          interviewData={selectedInterviewForFeedback}
        />
      </div>
    </div>
  );
};

export default FeedbackDashboard;